/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  Box, Typography, Button, Grid, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { formatDateOnly } from '@/utils/date';
import {
  setNumberOfRooms,
  addRoommate,
  removeRoommate,
  setFloorLevel,
  nextStep,
  prevStep,
  setStepVerified
} from '../habitationSlice';
import { FloorLevel, Roommate } from '../types';
import FloorIcon from '../components/FloorIcon';
import InfoText from '../components/InfoText';
import { useTranslation } from 'react-i18next';
import RoommateFormModal from '../components/RoommateFormModal';

const OptionBox = styled(Box)<{ selected: boolean }>`
  border: 2px solid ${({ selected }) => (selected ? '#2F6472' : '#ccc')};
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  position: relative;
  height: 100%;
  width: 210px;
  &:hover {
    border-color: #2F6472;
  }
  @media (max-width: 600px) {
      height: 120px;
      width: 100px;
      padding: 0px;
  }
`;

const RoommateLine = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: #2F6472;
`;

const IconButtonStyled = styled(IconButton)`
    margin: 15px;
    color: white;
    background-color: #8CCDCD;
    &:hover {
        background-color: #2F6472;
    }
    @media (max-width: 600px) {
        margin: 8px;
    }
`

const Spacing = styled.div`
  height: 30px;
  @media (max-width: 600px) {
    height: 10px;
  }
`;

const ButtonAddColoc = styled(Button)`
    color: white;
    border: solid, #2F6472;
    color: #2F6472;
    padding: 5px 20px;
    &:hover {
        background-color: #2F6472;
        color: white;
    }
`;


const AddIconStyled = styled(AddIcon)`
    color: white;
    background-color: #8CCDCD;
    border-radius: 100%;
`;


const HousingDetailsFullStep = () => {
    const { t } = useTranslation('habitation');

  const dispatch = useAppDispatch();
  const { numberOfRooms, roommates, floorLevel } = useAppSelector((state) => state.habitation);

  const [modalOpen, setModalOpen] = useState(false);
  const [roommateData, setRoommateData] = useState<Roommate>({
    civility: 'M',
    firstname: '',
    lastname: '',
    birthdate: ''
  });

  const handleRoomChange = (delta: number) => {
    dispatch(setNumberOfRooms(Math.max(1, numberOfRooms + delta)));
  };

  const handleNext = () => {
    dispatch(setStepVerified(3));
    dispatch(nextStep())
  };

  const handleAddRoommate = () => {
    if (roommateData.firstname && roommateData.lastname && roommateData.birthdate) {
      dispatch(addRoommate(roommateData));
      setModalOpen(false);
      setRoommateData({ civility: 'M', firstname: '', lastname: '', birthdate: '' });
    }
  };

  const handleSelectFloor = (level: FloorLevel) => {
    dispatch(setFloorLevel(level));
  };

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 2 }}>
        {/* Pièces du logement */}
        <Grid container spacing={0} mt={0}>
            <Grid item xs={6} sm={6}>
                <Typography variant="h6" gutterBottom>
                    {t('step3.rooms_title')}
                </Typography>
                <InfoText>{t('step3.rooms_hint')}</InfoText>
            </Grid>
            <Grid item xs={6} sm={6} container justifyContent="center" alignItems="center">
                <IconButtonStyled onClick={() => handleRoomChange(-1)}><RemoveIcon /></IconButtonStyled>
                <Typography fontSize={40} >{numberOfRooms}</Typography>
                <IconButtonStyled onClick={() => handleRoomChange(1)}><AddIcon /></IconButtonStyled>
            </Grid>
        </Grid>

        <Spacing />

        {/* Colocataires */}
        <Grid container spacing={2} mt={1} mb={0}>
            <Grid item xs={6} sm={6}>
                <Typography variant="h6">{t('step3.flatmates_title')}</Typography>
                <InfoText>{t('step3.flatmates_hint')}</InfoText>
            </Grid>
            <Grid item xs={6} sm={6} container justifyContent="center" alignItems="center" >
                <Box mb={1}>
                    <ButtonAddColoc
                        startIcon={<AddIconStyled />}
                        onClick={() => setModalOpen(true)}
                        >
                    {t('step3.add_flatmate')}
                    </ButtonAddColoc>
                </Box>
            </Grid>
             <Grid item xs={12} sm={12} justifyContent="left" alignItems="left">
                {roommates.map((r: Roommate, i: number) => (
                  <RoommateLine key={i}>
                    👤 {r.civility} {r.firstname} {r.lastname} {t('step3.flatmate_brithday')}{' '}
                    {r.birthdate
                      ? formatDateOnly(r.birthdate)
                      : ''}
                    <IconButton onClick={() => dispatch(removeRoommate(i))} size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </RoommateLine>
                ))}
            </Grid>
        </Grid>
        <Spacing />
        <Spacing />
        {/* Étages */}
        <Typography variant="h6" gutterBottom mb={0}>
          {t('step3.title')}
        </Typography>
        <InfoText>{t('step3.info')}</InfoText>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={4} sm={4} container justifyContent="center">
            <OptionBox selected={floorLevel === FloorLevel.ground} onClick={() => handleSelectFloor(FloorLevel.ground)}>
              <FloorIcon highlightedIndex={0} />
              <Typography mt={{sm: 2, xs: 0}}>{t('step3.ground')}</Typography>
            </OptionBox>
          </Grid>
          <Grid item xs={4} sm={4} container justifyContent="center">
            <OptionBox selected={floorLevel === FloorLevel.middle} onClick={() => handleSelectFloor(FloorLevel.middle)}>
              <FloorIcon highlightedIndex={1} />
              <Typography mt={{sm: 2, xs: 0}}>{t('step3.middle')}</Typography>
            </OptionBox>
          </Grid>
          <Grid item xs={4} sm={4} container justifyContent="center">
            <OptionBox selected={floorLevel === FloorLevel.top} onClick={() => handleSelectFloor(FloorLevel.top)}>
              <FloorIcon highlightedIndex={2} />
              <Typography mt={{sm: 2, xs: 0}}>{t('step3.last')}</Typography>
            </OptionBox>
          </Grid>
        </Grid>
  
        {/* Navigation */}
        <Box mt={4} display="flex" justifyContent="space-between">
          <Button onClick={() => dispatch(prevStep())} variant="outlined" sx={{ borderRadius: 20, px: 4 }}>
            {t('step1.back')}
          </Button>
          <Button
            onClick={() => handleNext()}
            variant="contained"
            disabled={numberOfRooms < 1 || floorLevel == null}
            sx={{
              borderRadius: 20,
              px: 4,
              backgroundColor: '#2F6472',
              '&:hover': { backgroundColor: 'white', color: '#2F6472' }
            }}
          >
          {t('step1.next')}
          </Button>
      </Box>

      {/* Modal ajout coloc */}
      <RoommateFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleAddRoommate}
        roommateData={roommateData}
        setRoommateData={setRoommateData}
        t={t}
      />
    </Box>
  );
};

export default HousingDetailsFullStep;
